import { cloneDeep } from "lodash";
import { useEffect, useState } from "react";

import { setTransactionColumnLock, submitToPcap } from "../../../../services/workbook.service";
import { DateTimeFormat } from "../../../../utils/helpers/format.helper";
import { getQuarterStartEndDates } from "../../../../utils/helpers/quarter.helper";
import { DataGridColDef } from "../../../../utils/types/listItems";
import { SubmitToPcapTransaction, TransactionColumn, Workbook } from "../../../../utils/types/workbook.type";
import { AllocationSpreadsheetManager } from "../AllocationWorkbook.manager";

type TransTypeGridRow = {
    id: string,
    transactionType: string,
    status?: string,
    locked?: boolean,
    submittable: boolean,
    submitted?: boolean,
    pcapTransactions: SubmitToPcapTransaction[],
    transTypeId: string
};

export enum SubmitTypes {
    Transactions = 'transactions', 
    UnlockTransactionTypes = 'Unlock-transaction-types'
}

export const useSubmitToPcapEffect = (workbook: Workbook, ssManager: AllocationSpreadsheetManager, quarterNumber: number) => {
    const [submitType, setSubmitType] = useState<SubmitTypes>(SubmitTypes.Transactions);
    const [gridRows, setGridRows] = useState<TransTypeGridRow[]>([]);
    const [refreshGrid, setRefreshGrid]= useState(0);
    const [selectionModel, setSelectionModel] = useState<string[]>([]);
    const [transactionDate, setTransactionDate] = useState(new Date());
    const [minQuarterDate, setMinQuarterDate] = useState(new Date());
    const [maxQuarterDate, setMaxQuarterDate] = useState(new Date());
    const [transDateErrorText, setTransDateErrorText] = useState('');
    const [needsSaving, setNeedsSaving] = useState(false);

    useEffect(() => {
        const qtrStartEnd = getQuarterStartEndDates(quarterNumber, workbook.startQuarterYear);

        setTransactionDate(qtrStartEnd.endDate);
        setMinQuarterDate(qtrStartEnd.startDate);
        setMaxQuarterDate(qtrStartEnd.endDate);

        const transactionColumns = workbook.transactionColumns.filter(col => !col.useArkTransactionValues && col.quarterNumber === quarterNumber && !col.isUserColumn);
        const initialGriwRows = transactionColumns.map((col, index) => {

            const gridRow: TransTypeGridRow = {
                id: index.toString(),
                transactionType: col.label,
                submittable: false,
                pcapTransactions: [],
                transTypeId: col.transTypeId
            };
            
            updateGridrowFromTransCol(gridRow);

            return gridRow;
        });

        setGridRows(initialGriwRows);
    }, []);

    useEffect(() => {
        gridRows.forEach(updateGridrowFromTransCol);
    }, [refreshGrid]);

    function updateGridrowFromTransCol(gridRow: TransTypeGridRow) {
        const col = workbook.transactionColumns.find(transCol => transCol.transTypeId === gridRow.transTypeId && transCol.quarterNumber === quarterNumber) as TransactionColumn;

        gridRow.locked = col.locked;

        if(col.locked) {
            gridRow.submittable = false;
            gridRow.status = 'Submitted';
            return gridRow;
        } else if (col.transSubmitError) {
            gridRow.submittable = true;
            gridRow.status = 'Error occurred. please try again or contact support';

            
            return gridRow;
        } else {
            gridRow.pcapTransactions = ssManager.getTransactionTypeValues(quarterNumber, col.transTypeId);
                
            if(gridRow.pcapTransactions.length === 0) {
                gridRow.submittable = false;
                gridRow.status = 'No transactions to submit';
                return gridRow;
            }

            const reconcileStatus = ssManager.getTransactionTypeGLRconcileStatus(col.quarterNumber, col.transTypeId);

            if(!reconcileStatus) {
                gridRow.submittable = false;
                gridRow.status = 'Allocated amounts do not equal Fund total';
                return gridRow;
            } 
        }

        gridRow.submittable = true;
        gridRow.status = 'Ready to Submit';
    }

    const headerList:DataGridColDef[] = 
    [
      {
        field: "transactionType",
        headerName: "Transaction Type",
        hide: false,
        index: 1,
        type: "string",
        sortable: false,
        align: "left",
        width: 80,
        disableReorder: true
      },
      {
        field: "status",
        headerName: "Status",
        hide: false,
        index: 2,
        type: "string",
        sortable: false,
        align: "left",
        width: 250,
        disableReorder: true,
      }
    ];

    function handleTransactionDateChange(value: any) {
        if(value.toString() !== 'Invalid Date') {
            setTransDateErrorText('');
        } else {
            const minDate = DateTimeFormat.shortDate((minQuarterDate));
            const maxDate = DateTimeFormat.shortDate((maxQuarterDate));

            setTransDateErrorText(`Must be between ${ minDate } and ${ maxDate }`);   
        }

        setTransactionDate(value as Date);
    }

    function handleSubmitButton() {
        if(submitType === SubmitTypes.Transactions) {
            handleSubmitToPcap();
        } else {
            handleUnlockTransactions();
        }

        setSelectionModel([]);
    }

    function handleSubmitToPcap() {
        const transDateStr = `${transactionDate.getUTCFullYear()}-${transactionDate.getUTCMonth()+1}-${transactionDate.getUTCDate()}`;

        const submittableRows = gridRows.filter(row => row.submittable);
        const gridRowsToSubmit = submittableRows.filter(row => selectionModel.find(selId => selId === row.id));

        if(gridRowsToSubmit.length > 0) {
            setNeedsSaving(true); 
        }
        
        let refreshCount = refreshGrid+1;

        gridRowsToSubmit.forEach(async gridRow => {
            const transCol = workbook.transactionColumns
                .find(transCol => transCol.quarterNumber === quarterNumber && transCol.transTypeId === gridRow.transTypeId) as TransactionColumn;
            
            gridRow.status = 'Submitting transactions';
            setGridRows(cloneDeep(gridRows));
            setRefreshGrid(refreshCount++);

            let success;

            try {
                const response = await submitToPcap(workbook.id as string, workbook.fund.id, transCol.transTypeId, transDateStr, gridRow.pcapTransactions);

                success = response.success;
            } catch {
                success = false;
            }

            if(success) {
                transCol.locked = true;
                transCol.transSubmitError = false;
            } else {
                transCol.locked = false;
                transCol.transSubmitError = true;
            }

            setGridRows(cloneDeep(gridRows));
            setRefreshGrid(refreshCount++);
        });

        setGridRows(cloneDeep(gridRows));
        setRefreshGrid(refreshCount++);
    }

    function handleUnlockTransactions() {
        const submittableRows = gridRows.filter(row => row.locked);
        const gridRowsToUnlock = submittableRows.filter(row => selectionModel.find(selId => selId === row.id));

        if(gridRowsToUnlock.length > 0) {
            setNeedsSaving(true); 
        }

        gridRowsToUnlock.forEach(gridRow => {
            const transCol = workbook.transactionColumns
                .find(transCol => transCol.quarterNumber === quarterNumber && transCol.transTypeId === gridRow.transTypeId) as TransactionColumn;

            transCol.locked = false;
            transCol.transSubmitError = false;

            const xxx = setTransactionColumnLock(workbook.id as string, gridRow.transTypeId, quarterNumber);
        });

        setGridRows(cloneDeep(gridRows));
        setRefreshGrid(refreshGrid+1);
    }

    return {
        submitType, setSubmitType,
        headerList, 
        gridRows, selectionModel, setSelectionModel,
        handleSubmitButton,
        transactionDate, handleTransactionDateChange, minQuarterDate, maxQuarterDate, transDateErrorText,
        needsSaving
    };
};